import { useEffect, useRef, useState } from "react";
import { App, Empty, Flex, Modal, QRCode, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import { qrCode as fetchEquipmentQrCode } from "@/apis/equipment";
import { EquipmentQrCode as EquipmentQrCodeInfo } from "@/types/equipment";

/** 设备二维码弹窗入参。 */
interface EquipmentQrCodeProps {
  open: boolean;
  equipmentId?: string;
  onCancel: () => void;
}

/** 加载并展示指定设备的二维码。 */
const EquipmentQrCode = ({
  open,
  equipmentId,
  onCancel,
}: EquipmentQrCodeProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [qrInfo, setQrInfo] = useState<EquipmentQrCodeInfo>();
  const [loading, setLoading] = useState(false);
  // 保存当前请求，以便切换设备或关闭弹窗时取消旧请求。
  const controllerRef = useRef<AbortController>();

  // 弹窗打开后加载二维码，并防止旧请求覆盖最新设备数据。
  useEffect(() => {
    controllerRef.current?.abort();

    if (!open || !equipmentId) {
      setQrInfo(undefined);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    controllerRef.current = controller;
    setQrInfo(undefined);
    setLoading(true);
    fetchEquipmentQrCode(equipmentId, controller)
      .then((result) => {
        if (controllerRef.current === controller) {
          setQrInfo(result);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          notification.error({
            message: t("equipment.qrCodeFetchFailed"),
            description: errorCodes[Number(error)] ?? String(error),
          });
        }
      })
      .finally(() => {
        if (controllerRef.current === controller) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [equipmentId, notification, open, t]);

  // 未配置独立二维码内容时，以资产编号生成二维码。
  const qrValue = qrInfo?.qrCode || qrInfo?.number;

  return (
    <Modal
      title={t("equipment.qrCode")}
      open={open}
      footer={null}
      destroyOnClose
      width={420}
      onCancel={onCancel}
    >
      {loading ? (
        <Flex justify="center" style={{ padding: 48 }}>
          <Spin />
        </Flex>
      ) : qrValue ? (
        <Flex vertical align="center" gap={12} style={{ padding: 16 }}>
          <QRCode value={qrValue} size={220} />
          <Typography.Text copyable>{qrInfo?.number}</Typography.Text>
        </Flex>
      ) : (
        <Empty description={t("equipment.noQrCodeData")} />
      )}
    </Modal>
  );
};

export default EquipmentQrCode;
